import PageShell from "@/components/PageShell";
import useSEO, { orgSchema, productSchema, breadcrumbSchema, faqSchema } from "@/lib/useSEO";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles } from "lucide-react";

const TIERS = [
  {
    name: "Single Block",
    size: "10×10 pixels · 1 block",
    price: "$50",
    per: "one-time",
    features: [
      "100 pixels of permanent grid real estate",
      "1× do-follow SEO backlink",
      "Editable logo / URL / description",
      "Live click + hover analytics",
      "Category placement + directory listing",
    ],
    cta: "Claim a single block",
    ctaTo: "/",
    highlight: false,
  },
  {
    name: "Medium Square",
    size: "20×20 pixels · 4 blocks",
    price: "$200",
    per: "one-time",
    features: [
      "400 pixels — 4× visibility",
      "Same do-follow backlink + directory listing",
      "Priority position in category filter results",
      "Larger, sharper logo rendering",
      "'Featured this week' eligibility",
    ],
    cta: "Claim 4 blocks",
    ctaTo: "/",
    highlight: true,
  },
  {
    name: "Large Square",
    size: "30×30 pixels · 9 blocks",
    price: "$450",
    per: "one-time",
    features: [
      "900 pixels — flagship placement",
      "Featured rotation in the site header",
      "Included in homepage 'Featured this week' rail",
      "Everything in Medium Square",
    ],
    cta: "Claim 9 blocks",
    ctaTo: "/",
    highlight: false,
  },
  {
    name: "Mega Billboard",
    size: "40×40 pixels · 16 blocks",
    price: "$800",
    per: "one-time",
    features: [
      "1,600 pixels of dominant real estate",
      "Guaranteed 'Featured this week' slot",
      "Directory: pinned to top of your category",
      "White-glove onboarding assistance",
      "Everything in Large Square",
    ],
    cta: "Claim 16 blocks",
    ctaTo: "/",
    highlight: false,
  },
];

const PRICING_FAQ = [
  {
    q: "Is this really a one-time payment?",
    a: "Yes. No monthly renewal, no auto-charges, no expiration. You pay once for lifetime placement on the pixel-empires.com grid.",
  },
  {
    q: "Will prices ever change?",
    a: "Prices only ever go UP as blocks sell. Today's $0.50/pixel is the floor. Buying early locks in the lowest rate PixelEmpire will ever offer.",
  },
  {
    q: "Are there hidden fees?",
    a: "No. The block price is all you pay. Stripe's processing fee is absorbed by us. There are no tax add-ons unless required by your local jurisdiction.",
  },
];

export default function Pricing() {
  useSEO({
    title: "Pricing — $0.50 per Pixel · Permanent Ad Blocks · PixelEmpire AI",
    description:
      "PixelEmpire pricing is transparent: $0.50 per pixel, $50 per 10×10 block, one-time payment for lifetime placement. Volume tiers up to 40×40 mega billboards. First 50 blocks are free.",
    path: "/pricing",
    jsonLd: [
      orgSchema,
      productSchema,
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Pricing", url: "/pricing" },
      ]),
      faqSchema(PRICING_FAQ),
    ],
  });

  return (
    <PageShell testId="page-pricing">
      <div className="pill" data-testid="pricing-pill">
        <Sparkles className="w-3.5 h-3.5" /> <span>Transparent, one-time pricing</span>
      </div>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-white" data-testid="pricing-h1">
        $0.50 per pixel. <span className="text-gradient">Forever.</span>
      </h1>
      <p className="mt-4 text-base sm:text-lg text-[var(--fg-muted)] max-w-3xl">
        Every block is a one-time payment. No subscriptions, no renewals, no surprises. Prices
        only ever increase as blocks sell out — today's rate is the lowest PixelEmpire will ever offer.
      </p>

      <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="pricing-tiers">
        {TIERS.map((t) => (
          <div
            key={t.name}
            className={`glass p-6 rounded-2xl relative ${
              t.highlight ? "border border-cyan-400/50 ring-1 ring-cyan-400/20" : ""
            }`}
            data-testid={`pricing-tier-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {t.highlight && (
              <div className="absolute -top-3 left-6 text-xs font-mono px-2 py-1 rounded-full bg-cyan-500 text-black font-semibold">
                Popular
              </div>
            )}
            <h2 className="font-display text-2xl text-white">{t.name}</h2>
            <div className="mt-1 text-xs font-mono text-[var(--fg-muted)]">{t.size}</div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-4xl text-white">{t.price}</span>
              <span className="text-xs text-[var(--fg-muted)] font-mono">{t.per}</span>
            </div>
            <ul className="mt-5 space-y-2 text-sm text-[var(--fg-muted)]">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link to={t.ctaTo} className="btn-primary mt-6 w-full justify-center" data-testid={`pricing-cta-${t.name.replace(/\s+/g, "-").toLowerCase()}`}>
              {t.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ))}
      </div>

      <section className="mt-14">
        <h2 className="font-display text-2xl text-white">Pricing FAQ</h2>
        <div className="mt-4 space-y-4">
          {PRICING_FAQ.map((f) => (
            <details key={f.q} className="glass p-5 rounded-xl group">
              <summary className="cursor-pointer font-semibold text-white flex items-center justify-between">
                {f.q}
                <span className="text-[var(--fg-muted)] group-open:rotate-45 transition">+</span>
              </summary>
              <p className="mt-3 text-sm text-[var(--fg-muted)] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
